import { defaultJobApplicationFilters, IJobApplicationDetailFilter } from '@modules/jobApplications/JobApplicationFilter';
import JobApplicationListContainer from '@modules/jobApplications/JobApplicationListContainer';
import { useGetJobApplications } from '@modules/jobApplications/hooks/useGetJobApplications';
import { useGetJobApplicationsKanban } from '@modules/jobApplications/hooks/useGetJobApplicationsKanban';
import { JobApplicationStatus } from '@modules/jobApplications/statuses';
import { usePageSize } from '@hooks/usePageSize';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const Container = styled(Box)(({ theme }) => ({
    padding: '0px 0px 16px',
    minHeight: 'calc(100dvh - 118px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    background: theme.palette.neutralBlack.main,
}));

/**
 * Job Applications Page
 *
 * This page displays all job applications across the system without being tied to a specific job offer.
 * It provides a comprehensive view of all applications with filtering, sorting, and pagination capabilities.
 *
 * Supports three view modes:
 * - List: Paginated table view
 * - Grid: Paginated card view
 * - Kanban: Infinite scroll columns by status
 */
const JobApplicationsPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const queryParam = searchParams.get('query') ?? '';
    const [query, setQuery] = useState(queryParam);
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'job-applications-page-size' });
    const [pagination, setPagination] = useState({ page: 0, pageSize });
    const [sortBy, setSortBy] = useState({ field: 'date', order: -1 });
    const [filter, setFilter] = useState(defaultJobApplicationFilters);

    const onQueryChange = useCallback((value: string) => {
        setQuery(value);
        console.log(value);
        // setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    const onSortChange = useCallback((newSortBy: { field: string; order: number }) => {
        setSortBy(newSortBy);
    }, []);

    const onFilterChange = useCallback((newFilter: IJobApplicationDetailFilter) => {
        setFilter(newFilter);
        setPagination(prev => ({ ...prev, page: 0 }));
    }, []);

    // Fetch data for list/grid view (paginated)
    const {
        jobApplications,
        loading: listLoading,
        isLoadingMore,
        loadMore,
        error: listError,
        totalDocs: listTotalDocs,
        totalPages,
        forceRefresh,
    } = useGetJobApplications(
        query,
        filter,
        sortBy,
        pagination.page,
        pagination.pageSize,
    );

    // Fetch data for kanban view (infinite scroll per status)
    const { kanbanData, loadMore: kanbanLoadMore } = useGetJobApplicationsKanban(query, filter, sortBy);

    const handleStatusUpdate = useCallback((applicationId: number, newStatus: JobApplicationStatus) => {
        // TODO: Implement GraphQL mutation to update job application status
        console.log('Update job application status:', { applicationId, newStatus });
    }, []);

    if (listError) {
        console.error('Error fetching job applications:', listError);
        return (
            <Container>
                <Box sx={{ padding: '24px' }}>
                    <div>
                        Error fetching job applications <pre>{JSON.stringify(listError, null, 2)}</pre>
                    </div>
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <JobApplicationListContainer
                data={jobApplications}
                kanbanData={kanbanData}
                loading={listLoading}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
                totalDocs={listTotalDocs}
                totalPages={totalPages}
                meta={{ totalDocs: listTotalDocs, page: pagination.page + 1, limit: pagination.pageSize }}
                currentPage={pagination.page + 1}
                itemsPerPage={pagination.pageSize}
                onPageChange={page => setPagination(prev => ({ ...prev, page: page - 1 }))}
                onItemsPerPageChange={limit => {
                    storePageSize(limit);
                    setPagination({ page: 0, pageSize: limit });
                }}
                onQueryChange={onQueryChange}
                onSortChange={onSortChange}
                onFilterChange={onFilterChange}
                filter={filter}
                onLoadMoreKanban={kanbanLoadMore}
                onStatusUpdate={handleStatusUpdate}
                onRefresh={forceRefresh}
                initialQuery={queryParam}
            />
        </Container>
    );
};

export default JobApplicationsPage;
